import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import dayjs from 'dayjs';

/**
 * Date Carousel component using shadcn/ui
 * @param {Object} props - Component props
 * @param {string} props.selectedDate - Currently selected date in YYYY-MM-DD format
 * @param {Function} props.onDateChange - Callback when date is selected
 * @param {number} props.daysToShow - Number of days to show (default: calculated based on screen width)
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DateCarousel component
 */
const DateCarousel = ({
  selectedDate,
  onDateChange,
  daysToShow,
  className = ''
}) => {
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [calculatedDaysToShow, setCalculatedDaysToShow] = useState(daysToShow || 7);
  const [totalDays] = useState(30); // Show 30 days total for navigation

  // Always show exactly 12 dates at one glance
  const calculateDaysToShow = useCallback(() => {
    return 12; // Fixed number of dates to show
  }, []);

  // Set days to show (fixed at 12)
  useEffect(() => {
    const newDaysToShow = calculateDaysToShow();
    setCalculatedDaysToShow(newDaysToShow);
  }, [calculateDaysToShow]);

  // Generate array of dates starting from today
  const generateDates = () => {
    const dates = [];
    const today = dayjs();

    for (let i = 0; i < totalDays; i++) {
      const date = today.add(i, 'day');
      dates.push({
        value: date.format('YYYY-MM-DD'),
        display: date.format('MMM DD'),
        dayName: date.format('ddd'),
        isToday: i === 0,
        dayNumber: date.format('DD'),
        month: date.format('MMM')
      });
    }

    return dates;
  };

  const dates = generateDates();

  // Don't automatically set today as default - let the parent component handle initial state
  // This allows for proper deselection functionality
  useEffect(() => {
    // Only set today as default on initial mount if no date is provided and onDateChange expects it
    // This is now handled by the parent component (TripInfoContext)
  }, [selectedDate, onDateChange]);

  // Check scroll position
  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 5); // Small threshold to avoid flickering
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  // Handle scroll
  const scroll = (direction) => {
    if (scrollRef.current) {
      const dateItemWidth = 68; // Width of each date item including gap
      const scrollAmount = dateItemWidth * Math.max(1, Math.floor(calculatedDaysToShow / 2)); // Scroll by half the visible items
      const newScrollLeft = scrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Auto-scroll to selected date
  useEffect(() => {
    if (selectedDate && scrollRef.current) {
      const selectedIndex = dates.findIndex(date => date.value === selectedDate);
      if (selectedIndex !== -1) {
        const dateElement = scrollRef.current.children[selectedIndex];
        if (dateElement) {
          dateElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  }, [selectedDate, dates]);

  // Add scroll event listener
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition(); // Initial check
      
      return () => {
        scrollElement.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, []);

  return (
    <>
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div ref={containerRef} className={cn("relative flex items-center w-full max-w-full overflow-hidden", className)}>
        {/* Left scroll button */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "absolute left-0 z-10 h-8 w-8 p-0 rounded-full bg-white shadow-md border flex-shrink-0",
            !canScrollLeft && "opacity-50 cursor-not-allowed"
          )}
          onClick={() => scroll('left')}
          disabled={!canScrollLeft}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Date carousel */}
        <div
          ref={scrollRef}
          className="flex gap-1.5 overflow-x-auto px-8 py-2 w-full scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
        {dates.map((date) => (
          <button
            key={date.value}
            onClick={() => {
              // If clicking on the already selected date, deselect it
              if (selectedDate === date.value) {
                onDateChange(null);
              } else {
                onDateChange(date.value);
              }
            }}
            className={cn(
              "flex flex-col items-center justify-center min-w-[55px] max-w-[55px] h-14 px-2 py-1.5 rounded-lg border transition-all duration-200 hover:shadow-md flex-shrink-0",
              selectedDate === date.value
                ? "bg-blue-500 text-white border-blue-500 shadow-md"
                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50",
              date.isToday && selectedDate !== date.value && "border-blue-200 bg-blue-50"
            )}
          >
            <span className={cn(
              "text-[10px] font-medium",
              selectedDate === date.value ? "text-white" : "text-gray-500"
            )}>
              {date.dayName}
            </span>
            <span className={cn(
              "text-xs font-semibold",
              selectedDate === date.value ? "text-white" : "text-gray-900"
            )}>
              {date.dayNumber}
            </span>
            <span className={cn(
              "text-xs",
              selectedDate === date.value ? "text-blue-100" : "text-gray-500"
            )}>
              {date.month}
            </span>
          </button>
        ))}
      </div>

      {/* Right scroll button */}
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "absolute right-0 z-10 h-8 w-8 p-0 rounded-full bg-white shadow-md border flex-shrink-0",
          !canScrollRight && "opacity-50 cursor-not-allowed"
        )}
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      </div>
    </>
  );
};

export default DateCarousel;
